import type { ReactNode } from "react";

import { cx } from "@/lib/cx";
import { SelectorIcon } from "@/ui/icons";

export type ToolbarProps = {
  title: string;
  /** Presentation for the title — this is where typewriterProps goes. */
  titleContent?: ReactNode;
  /** Makes the title a menu trigger. Kanban's project switcher needs this. */
  titleMenu?: (title: ReactNode) => ReactNode;
  meta?: ReactNode;
  leading?: ReactNode;
  actions?: ReactNode;
  /** A second 40/44px row under the bar that scrolls away. */
  accessory?: ReactNode;
  className?: string;
  /** Pages use h1; an overlay nested in a page uses h2. */
  headingLevel?: 1 | 2;
};

/**
 * The macOS window toolbar shared by every compact surface.
 *
 * `PageHeader` used to be an iOS navigation bar with a collapsing 34px large
 * title and a second hidden heading. This is one 64px app bar, it does not
 * collapse because it has nothing to collapse into, and the `<h1>` uses the
 * shared 24/600 title step. `titleContent` keeps presentation such as
 * the typewriter on that real heading; `titleMenu` wraps the same node in one
 * interactive trigger rather than creating a second title.
 *
 * It bleeds to the edges of the scroll column by cancelling
 * `--busee-page-inset` — the variable the shell publishes on the element
 * carrying the page padding — exactly as the old `PageHeader` did, and reserves
 * the corner the shell parks the account chrome in on a phone. An `accessory`
 * is a separate 40/44px row below the sticky bar, so it scrolls away.
 */
export function Toolbar({
  title,
  titleContent,
  titleMenu,
  meta,
  leading,
  actions,
  accessory,
  className,
  headingLevel = 1,
}: ToolbarProps) {
  const headingContent = titleContent ?? title;
  const presentedTitle = titleMenu
    ? titleMenu(
        <>
          <span className="ps1-pill truncate">{headingContent}</span>
          <SelectorIcon className="size-4 shrink-0 self-center text-subtle" />
        </>,
      )
    : <span className="ps1-pill truncate">{headingContent}</span>;

  return (
    <>
      <div
        className={cx(
          "sticky top-0 z-20 -mt-pad-tight shrink-0",
          "mx-[calc(var(--busee-page-inset,0px)*-1)]",
          "pixel-toolbar ps1-toolbar material-thin hairline-b",
          accessory ? "ps1-toolbar--accessory" : "mb-3",
          className,
        )}
      >
        <div
          className="flex min-h-16 items-center gap-3 px-[var(--busee-page-inset,0px)] py-2"
          style={{
            paddingInlineEnd:
              "calc(var(--busee-page-inset, 0px) + var(--busee-chrome-inset-end, 0px))",
          }}
        >
          {leading ? <span className="flex shrink-0 items-center gap-1">{leading}</span> : null}
          <div className="flex min-w-0 flex-1 flex-col justify-center">
            {headingLevel === 1 ? (
              <h1 aria-label={title} className="flex min-w-0 text-title text-ink">
                {presentedTitle}
              </h1>
            ) : (
              <h2 aria-label={title} className="flex min-w-0 text-title text-ink">
                {presentedTitle}
              </h2>
            )}
            {meta ? (
              <span className="ps1-meta min-w-0 truncate">{meta}</span>
            ) : null}
          </div>
          {actions ? <span className="flex shrink-0 items-center gap-2">{actions}</span> : null}
        </div>
      </div>

      {accessory ? (
        <div
          className={cx(
            // `row-height`, never a fixed height. The strip is 40px with a
            // mouse and 44px for touch when it holds one row of controls, but a
            // surface that has not been converted yet hands it a taller block,
            // and a fixed height silently overflows it onto the content below
            // instead of pushing that content down. A minimum keeps the rung
            // without making the box a clipper.
            "ps1-accessory mb-3 flex row-height shrink-0 items-center py-1",
            "mx-[calc(var(--busee-page-inset,0px)*-1)] hairline-b",
            // Deliberately NOT `--busee-chrome-inset-end`. The floating account
            // corner is `absolute top-0 h-navbar` — it covers the 36px bar
            // above and stops exactly where this strip begins, so reserving
            // 64px here was 64px given to nothing. On a 320px phone that is a
            // fifth of the window, and it was the whole reason the board's
            // filter field collapsed to a bare magnifier under the lane menu
            // and the day stepper ran past the day list's right edge.
            "px-[var(--busee-page-inset,0px)]",
          )}
        >
          <div className="min-w-0 flex-1">{accessory}</div>
        </div>
      ) : null}
    </>
  );
}
